/* Keep server-rendered ratings/reviews; hydrate the full app widget near the viewport. */
(() => {
  if (window.slDeferredReviews) return;
  const pending = new Set();
  const loading = new Map();
  const cssLoads = new Map();
  let observer;
  let manifestPromise;
  function getBase() {
    const configured = window.jdgm?.CDN_BASE_URL;
    if (configured) return configured;
    const loader = [...document.scripts].find(s => /\/judgeme-[^/]+\/assets\/loader\.js/.test(s.src));
    return loader ? new URL('.', loader.src).href : null;
  }
  async function waitForBase() {
    for (let i = 0; i < 100; i++) {
      const base = getBase();
      if (base) return base;
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    throw new Error('Review app base URL unavailable');
  }
  function loadCSS(url) {
    if ([...document.querySelectorAll('link[rel="stylesheet"]')].some(l => l.href === url)) return Promise.resolve();
    if (cssLoads.has(url)) return cssLoads.get(url);
    const promise = new Promise((resolve, reject) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet'; link.href = url;
      const fail = () => { clearTimeout(timer); cssLoads.delete(url); link.remove(); reject(new Error('Review stylesheet unavailable')); };
      const timer = setTimeout(fail, 20000);
      link.onload = () => { clearTimeout(timer); resolve(); };
      link.onerror = fail;
      document.head.appendChild(link);
    });
    cssLoads.set(url, promise);
    return promise;
  }
  async function loadEntry(base, entry, key) {
    const url = new URL(entry, base);
    if (url.origin !== new URL(base).origin || !/\.js$/.test(url.pathname)) throw new Error('Invalid review entry');
    if (!manifestPromise) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 15000);
      manifestPromise = fetch(new URL('manifest.json', base), {signal: controller.signal}).then(r => {
      if (!r.ok) throw new Error('Review manifest unavailable');
      return r.json();
    }).catch(error => { manifestPromise = null; throw error; }).finally(() => clearTimeout(timer));
    }
    const manifest = await manifestPromise;
    if (!manifest[key]) throw new Error('Review entry missing from manifest');
    const seen = new Set(), styles = new Set();
    function collect(k) {
      if (seen.has(k)) return;
      seen.add(k);
      const item = manifest[k];
      if (!item) return;
      (item.css || []).forEach(path => styles.add(new URL(path, base).href));
      (item.imports || []).forEach(collect);
    }
    collect(key);
    await Promise.all([...styles].map(loadCSS));
    await new Promise((resolve, reject) => {
      const existing = [...document.scripts].find(s => s.src === url.href);
      if (existing) { resolve(); return; }
      const script = document.createElement('script');
      script.type = 'module'; script.src = url.href;
      const fail = () => { clearTimeout(timer); script.remove(); reject(new Error('Review module unavailable')); };
      const timer = setTimeout(fail, 20000);
      script.onload = () => { clearTimeout(timer); resolve(); };
      script.onerror = fail;
      document.head.appendChild(script);
    });
  }
  async function hydrate(target) {
    const entry = target.dataset.slReviewEntry;
    if (!entry || loading.has(entry)) return loading.get(entry);
    const group = [...pending].filter(el => el.dataset.slReviewEntry === entry);
    group.forEach(el => {
      el.dataset.entryPoint = entry;
      el.dataset.slReviewState = 'loading';
      observer?.unobserve(el);
    });
    const task = (async () => {
      try {
        await loadEntry(await waitForBase(), entry, target.dataset.entryKey);
        group.forEach(el => { el.dataset.slReviewState = 'ready'; pending.delete(el); });
      } catch (error) {
        loading.delete(entry);
        group.forEach(el => {
          el.dataset.slReviewState = 'error';
          // Retain SSR reviews and offer an explicit retry rather than an empty widget.
          if (!el.querySelector('[data-sl-review-retry]')) {
            const button = document.createElement('button');
            button.type = 'button'; button.dataset.slReviewRetry = '';
            button.textContent = 'Wczytaj interaktywne opinie';
            button.addEventListener('click', () => { button.remove(); hydrate(el); });
            el.appendChild(button);
          }
        });
        console.warn('[Solve Labs] Deferred reviews:', error.message);
      }
    })();
    loading.set(entry, task);
    return task;
  }
  function init(root = document) {
    root.querySelectorAll('[data-sl-review-entry]').forEach(el => {
      if (el.dataset.slReviewState) return;
      el.dataset.slReviewState = 'pending'; pending.add(el);
      if (observer) observer.observe(el); else hydrate(el);
    });
    if (/^#(judgeme|judgeme_product_reviews)/.test(location.hash) || /judgeme_(token|review_uuid|dynamic_form|follow_up_token|upload_pictures|pre_verified)/.test(location.search)) {
      pending.forEach(hydrate);
    }
  }
  if ('IntersectionObserver' in window) observer = new IntersectionObserver(entries => {
    entries.forEach(entry => { if (entry.isIntersecting) hydrate(entry.target); });
  }, {rootMargin: '1200px 0px', threshold: 0});
  document.addEventListener('click', event => {
    if (event.target instanceof Element && event.target.closest('.jdgm-prev-badge,a[href="#judgeme_product_reviews"],a[href="#judgeme"]')) pending.forEach(hydrate);
  }, {capture: true});
  window.addEventListener('hashchange', () => {
    if (/^#(judgeme|judgeme_product_reviews)/.test(location.hash)) pending.forEach(hydrate);
  });
  document.addEventListener('shopify:section:load', event => init(event.target));
  window.slDeferredReviews = {init};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), {once:true}); else init();
})();
